import { useMemo } from 'react'
import DeckGL from '@deck.gl/react'
import { OrbitView } from '@deck.gl/core'
import { LineLayer, PathLayer, PointCloudLayer, ScatterplotLayer } from '@deck.gl/layers'
import type { MapCatalogEntry, Point3D, PointCloudFrame, Pose3D, Quaternion, StaticMapPointCloud } from '../types'

type Position3D = [number, number, number]
type Color = [number, number, number, number]
interface Segment {
  source: Position3D
  target: Position3D
  color: Color
  width: number
  label?: string
}
interface Path3D {
  path: Position3D[]
  label: string
}
interface Marker3D {
  position: Position3D
  label: string
}

function turbo(value: number): Color {
  const normalized = Math.max(0, Math.min(1, value / 255))
  return [Math.round(45 + 210 * normalized), Math.round(210 - 90 * normalized), Math.round(235 - 190 * normalized), 235]
}

function rotate([vx, vy, vz]: Position3D, source: Quaternion): Position3D {
  const norm = Math.hypot(source.x, source.y, source.z, source.w) || 1
  const qx = source.x / norm
  const qy = source.y / norm
  const qz = source.z / norm
  const qw = source.w / norm
  const ix = qw * vx + qy * vz - qz * vy
  const iy = qw * vy + qz * vx - qx * vz
  const iz = qw * vz + qx * vy - qy * vx
  const iw = -qx * vx - qy * vy - qz * vz
  return [ix * qw + iw * -qx + iy * -qz - iz * -qy, iy * qw + iw * -qy + iz * -qx - ix * -qz, iz * qw + iw * -qz + ix * -qy - iy * -qx]
}

function translated(pose: Pose3D, local: Position3D): Position3D {
  const value = rotate(local, pose.orientation)
  return [pose.x + value[0], pose.y + value[1], pose.z + value[2]]
}

function robotSegments(pose?: Pose3D): Segment[] {
  if (!pose) return []
  const hx = 0.35
  const hy = 0.225
  const hz = 0.15
  const localCorners: Position3D[] = [
    [-hx, -hy, -hz],
    [hx, -hy, -hz],
    [hx, hy, -hz],
    [-hx, hy, -hz],
    [-hx, -hy, hz],
    [hx, -hy, hz],
    [hx, hy, hz],
    [-hx, hy, hz],
  ]
  const corners = localCorners.map((point) => translated(pose, point))
  const edges = [
    [0, 1],
    [1, 2],
    [2, 3],
    [3, 0],
    [4, 5],
    [5, 6],
    [6, 7],
    [7, 4],
    [0, 4],
    [1, 5],
    [2, 6],
    [3, 7],
  ]
  const result: Segment[] = edges.map(([from, to]) => ({
    source: corners[from],
    target: corners[to],
    color: [238, 255, 252, 245],
    width: 2,
    label: '6DoF 机器人模型',
  }))
  const origin: Position3D = [pose.x, pose.y, pose.z]
  result.push(
    { source: origin, target: translated(pose, [0.8, 0, 0]), color: [255, 92, 92, 255], width: 4, label: 'robot X' },
    { source: origin, target: translated(pose, [0, 0.6, 0]), color: [83, 221, 160, 255], width: 3, label: 'robot Y' },
    { source: origin, target: translated(pose, [0, 0, 0.6]), color: [91, 166, 255, 255], width: 3, label: 'robot Z' },
  )
  return result
}

function boundsOf(points: Point3D[], trajectory: Point3D[], staticPoints: Point3D[], pose?: Pose3D) {
  const all = [...points, ...trajectory, ...staticPoints]
  if (pose) all.push({ x: pose.x, y: pose.y, z: pose.z, intensity: 0 })
  if (!all.length) return { target: [0, 0, 0] as Position3D, span: 1 }
  let minX = all[0].x
  let maxX = all[0].x
  let minY = all[0].y
  let maxY = all[0].y
  let minZ = all[0].z
  let maxZ = all[0].z
  for (const point of all.slice(1)) {
    minX = Math.min(minX, point.x)
    maxX = Math.max(maxX, point.x)
    minY = Math.min(minY, point.y)
    maxY = Math.max(maxY, point.y)
    minZ = Math.min(minZ, point.z)
    maxZ = Math.max(maxZ, point.z)
  }
  return { target: [(minX + maxX) / 2, (minY + maxY) / 2, (minZ + maxZ) / 2] as Position3D, span: Math.max(maxX - minX, maxY - minY, maxZ - minZ, 0.1) }
}

export function PointCloudCanvas({
  cloud,
  staticMap,
  staticCloud,
  loading,
  frameAvailable,
}: {
  cloud?: PointCloudFrame
  staticMap?: MapCatalogEntry
  staticCloud?: StaticMapPointCloud
  loading: boolean
  frameAvailable: boolean
}) {
  const { layers, target, zoom } = useMemo(() => {
    const points = cloud?.points ?? []
    const trajectory = cloud?.trajectory ?? []
    const staticPoints = staticCloud?.points ?? []
    const pose = cloud?.pose?.pose
    const bounds = boundsOf(points, trajectory, staticPoints, pose)
    const worldAxes: Segment[] = [
      { source: [0, 0, 0], target: [1, 0, 0], color: [255, 92, 92, 255], width: 3, label: 'map X' },
      { source: [0, 0, 0], target: [0, 1, 0], color: [83, 221, 160, 255], width: 3, label: 'map Y' },
      { source: [0, 0, 0], target: [0, 0, 1], color: [91, 166, 255, 255], width: 3, label: 'map Z' },
    ]
    const paths: Path3D[] = trajectory.length ? [{ path: trajectory.map((point) => [point.x, point.y, point.z]), label: '6DoF 历史轨迹' }] : []
    const markers: Marker3D[] = pose ? [{ position: [pose.x, pose.y, pose.z], label: `map ${pose.mapId || '-'}` }] : []
    return {
      target: bounds.target,
      zoom: points.length ? Math.max(-1, Math.min(8, 9 - Math.log2(bounds.span))) : 3.2,
      layers: [
        new PointCloudLayer<Point3D>({
          id: `static-map-${staticCloud?.mapId ?? 'none'}`,
          data: staticPoints,
          getPosition: (point) => [point.x, point.y, point.z],
          getColor: [74, 158, 232, 145],
          pointSize: 1.4,
          pickable: true,
        }),
        new PointCloudLayer<Point3D>({
          id: 'pointcloud-3d',
          data: points,
          getPosition: (point) => [point.x, point.y, point.z],
          getColor: (point) => turbo(point.intensity),
          pointSize: 2,
          pickable: true,
        }),
        new PathLayer<Path3D>({
          id: 'pose-trajectory-3d',
          data: paths,
          getPath: (item) => item.path,
          getColor: [82, 214, 173, 245],
          getWidth: 3,
          widthUnits: 'pixels',
          pickable: true,
        }),
        new LineLayer<Segment>({
          id: 'pointcloud-axes',
          data: [...worldAxes, ...robotSegments(pose)],
          getSourcePosition: (item) => item.source,
          getTargetPosition: (item) => item.target,
          getColor: (item) => item.color,
          getWidth: (item) => item.width,
          widthUnits: 'pixels',
          pickable: true,
        }),
        new ScatterplotLayer<Marker3D>({
          id: 'robot-6dof-center',
          data: markers,
          getPosition: (item) => item.position,
          getFillColor: [48, 221, 194, 255],
          getRadius: 0.11,
          radiusUnits: 'meters',
          radiusMinPixels: 4,
          stroked: true,
          getLineColor: [255, 255, 255, 255],
          getLineWidth: 2,
          pickable: true,
        }),
      ],
    }
  }, [cloud, staticCloud])

  const pose = cloud?.pose?.pose
  return (
    <div className="pointcloud-canvas">
      <DeckGL
        key={`${staticCloud?.mapId ?? 'no-map'}-${cloud?.timestampNs ?? 'empty'}`}
        views={new OrbitView({ id: 'pointcloud', controller: true, orbitAxis: 'Z' })}
        initialViewState={{ target, rotationX: 28, rotationOrbit: 32, zoom, minZoom: -2, maxZoom: 12 }}
        layers={layers}
        controller
        getTooltip={({ object }) =>
          object && 'x' in object
            ? { text: `x ${object.x.toFixed(2)} · y ${object.y.toFixed(2)} · z ${object.z.toFixed(2)} · I ${object.intensity.toFixed(0)}` }
            : object?.label
              ? { text: object.label }
              : null
        }
      />
      <div className="map-axis">
        <span>X</span>
        <span>Y</span>
        <span>Z</span>
        <span>{cloud?.frame ?? 'map'} · meter</span>
      </div>
      <div className="pointcloud-pose">
        {pose ? `6DoF · ${pose.x.toFixed(2)}, ${pose.y.toFixed(2)}, ${pose.z.toFixed(2)}` : '6DoF 暂不可用'} · Map{' '}
        {staticMap ? `${staticMap.mapId} ${staticMap.name}` : pose?.mapId || '-'}
      </div>
      <div className="map-watermark">3D STATIC MAP + CLOUD + POSE + TRAJECTORY · LAST FRAME ≤ PLAYHEAD</div>
      {!cloud && !staticCloud && (
        <div className="pointcloud-loading">{loading ? '正在读取 MCAP 与静态地图索引…' : frameAvailable ? '点云帧暂不可用' : '当前时刻之前没有点云帧'}</div>
      )}
    </div>
  )
}
